/**
 * TimelineToggleWithPopper Component
 *
 * Common wrapper for timeline control toggles that show a popper on hover.
 * Used for collapsed (!isExpanded) state where individual controls are grouped.
 */

import React, { useState, useRef } from 'react'
import { Box, Popper, Grow, ClickAwayListener, ToggleButton } from '@mui/material'
import { TpControlTooltip } from '../../../../../components/tp-control-tooltip'
import { POPPER_OFFSET_MODIFIER, TOGGLE_TIMEOUT_DELAY } from './timeline-controls-constants'

/**
 * TimelineToggleWithPopper - Hover-triggered toggle with popper content
 *
 * @param {Object} props
 * @param {string} props.title - Tooltip title text
 * @param {React.ReactElement} props.icon - Icon component to display in button
 * @param {number} [props.tooltipOffsetBottom=24] - Tooltip offset from button
 * @param {React.ReactElement} props.children - Content to show in popper (typically a toggle button group)
 */
export const TimelineToggleWithPopper = ({ title, icon, tooltipOffsetBottom = 24, children }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const closeTimeoutRef = useRef(null)

  const handleOpen = (e) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null)
    }, TOGGLE_TIMEOUT_DELAY)
  }

  const handlePopperEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
  }

  const open = Boolean(anchorEl)

  return (
    <ToggleButton
      selected={open}
      onClick={(e) => setAnchorEl(prev => prev === null ? e.currentTarget : null)}
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <TpControlTooltip title={title} offsetBottom={tooltipOffsetBottom}>
        {icon}
      </TpControlTooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="top"
        transition
        modifiers={[POPPER_OFFSET_MODIFIER]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Box>
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                {children}
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
    </ToggleButton>
  )
}
